import React, { useState, useEffect, useCallback } from 'react';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import TestHero from './components/TestHero';
import SimpleHeroTest from './components/SimpleHeroTest';
import type { HeroSectionData } from './components/Hero';
import { Services } from './components/Services';
import { Portfolio } from './components/Portfolio';
import { SectionAI } from './components/WorksSection';
import ContactFormPage from './components/ContactFormPage';
import { Footer } from './components/updatedFooter';
import { Preloader } from './components/Preloader';
import { Cursor } from './components/Cursor';
import { ProjectsPage } from './components/ProjectsPage';
import { ServicesPage } from './components/ServicesPage';
import AboutPage from './components/AboutPage';
import ModernDashboard from './components/Dashboard/ModernDashboard';
import Login from './components/Login';
import ProjectDetail from './components/ProjectDetail';
import PortfolioDetailPage from './components/PortfolioDetailPage';
import ServiceDetail from './components/ServiceDetail';
import SingleProjectPage from './components/SingleProjectPage';
import PageViewer from './components/PageViewer';
import PageBuilder from './components/Dashboard/PageBuilder/PageBuilder';
import DynamicPage from './components/DynamicPage';
import Error404 from './components/Error404';
import HeroReelPage from './components/HeroReelPage';
import ScrollToTop from './components/ScrollToTop';
import FloatingWhatsApp from './components/FloatingWhatsApp';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { heroApi, assetUrl } from './services/api';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';


const ConditionalNavbar: React.FC<{ onContactClick: () => void }> = ({ onContactClick }) => {
  const location = useLocation();
  const isDashboard = location.pathname.startsWith('/dashboard');
  const isLogin = location.pathname === '/login';

  if (isDashboard || isLogin) return null;

  return <Navbar onContactClick={onContactClick} />;
};

const App: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [videoLoaded, setVideoLoaded] = useState(true); // Set to true by default to avoid blocking
  const [videoBlobUrl, setVideoBlobUrl] = useState<string | null>(null);

  // Routing-based dashboard; no view state toggling
  type AppHeroSection = HeroSectionData & {
    display_order: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    video_url?: string | null;
    video_type?: 'background' | 'foreground';
    video_settings?: unknown;
    use_video?: boolean;
  };
  const [heroContent, setHeroContent] = useState<AppHeroSection[]>([]);
  const [isRevealing, setIsRevealing] = useState(true);

  // Preload hero video immediately when data is available
  const preloadHeroVideo = async (sections: AppHeroSection[]) => {
    try {
      if (!sections || !sections.length) {
        setVideoLoaded(true);
        return;
      }
      // Find the first section that uses a video
      const firstVideoSection = sections.find(s => s.use_video && s.video_url);
      if (firstVideoSection && firstVideoSection.video_url) {
        const href = assetUrl(firstVideoSection.video_url);
        if (href) {
          console.log('🚀 Starting video blob fetch:', href);
          try {
            const response = await fetch(href);
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            setVideoBlobUrl(objectUrl);
            console.log('✅ Video blob ready:', objectUrl);
          } catch (err) {
            console.warn('Video fetch failed, falling back to stream', err);
          }
        }
      }
    } catch (e) {
      console.warn('Failed to preload hero video:', e);
    } finally {
      setVideoLoaded(true);
    }
  };

  const updateHeroFromDashboard: (d: AppHeroSection[]) => void = (data) => {
    setHeroContent(data as AppHeroSection[]);
    // Don't block on dashboard updates
    try { localStorage.setItem('heroSections', JSON.stringify(data)); } catch { void 0; }
  };
  type ThemeConfig = {
    general?: { mode?: 'light'|'dark' };
    header?: { bg_color?: string; text_color?: string; height?: number; logo_size?: number; logo_position?: 'left'|'center'|'right'; menu_font?: string; menu_size?: number; menu_hover_color?: string; shadow_color?: string; shadow_intensity?: number };
    main?: { primary_color?: string; secondary_color?: string; background_color?: string; text_color?: string; button_color?: string; button_hover_color?: string; card_bg?: string };
    footer?: { bg_color?: string; text_color?: string; link_color?: string; link_hover_color?: string; icon_color?: string; height?: number; layout?: 'columns'|'stacked'|'minimal' };
    typography?: { font_family?: string; h1_size?: number; h2_size?: number; h3_size?: number; body_size?: number; weight_normal?: number; weight_bold?: number };
    radius?: { base?: number; button?: number };
    shadow?: { color?: string; intensity?: number };
    animation?: { speed?: number };
    spacing?: { base?: number; section_padding?: number };
    custom_css?: { css?: string };
  };
  const applyTheme = useCallback((t: Partial<ThemeConfig>|Record<string, unknown>) => {
    const root = document.documentElement;
    if (t && typeof t === 'object' && !('general' in t)) {
      if (t.background_color) root.style.setProperty('--bg-color', t.background_color);
      if (t.text_color) root.style.setProperty('--text-color', t.text_color);
      if (t.primary_color) root.style.setProperty('--primary-color', t.primary_color);
      if (t.secondary_color) root.style.setProperty('--secondary-color', t.secondary_color);
      if (t.button_color) root.style.setProperty('--button-color', t.button_color);
      if (t.button_hover_color) root.style.setProperty('--button-hover-color', t.button_hover_color);
      if (t.header_color) root.style.setProperty('--header-bg', t.header_color);
      if (t.footer_color) root.style.setProperty('--footer-bg', t.footer_color);
      if (t.mode) root.style.setProperty('--mode', t.mode);
      return;
    }
    const g = t?.general || {}; if (g.mode) root.style.setProperty('--mode', String(g.mode));
    const h = t?.header || {}; if (h.bg_color) root.style.setProperty('--header-bg', String(h.bg_color)); if (h.text_color) root.style.setProperty('--text-color', String(h.text_color)); if (h.height) root.style.setProperty('--header-height', `${h.height}px`); if (h.logo_size) root.style.setProperty('--logo-size', `${h.logo_size}px`); if (h.logo_position) root.style.setProperty('--logo-position', String(h.logo_position)); if (h.menu_font) root.style.setProperty('--menu-font', String(h.menu_font)); if (h.menu_size) root.style.setProperty('--menu-size', `${h.menu_size}px`); if (h.menu_hover_color) root.style.setProperty('--menu-hover-color', String(h.menu_hover_color)); if (h.shadow_color) root.style.setProperty('--shadow-color', String(h.shadow_color)); if (h.shadow_intensity != null) root.style.setProperty('--shadow-intensity', String(h.shadow_intensity));
    const m = t?.main || {}; if (m.primary_color) root.style.setProperty('--primary-color', String(m.primary_color)); if (m.secondary_color) root.style.setProperty('--secondary-color', String(m.secondary_color)); if (m.background_color) root.style.setProperty('--bg-color', String(m.background_color)); if (m.text_color) root.style.setProperty('--text-color', String(m.text_color)); if (m.button_color) root.style.setProperty('--button-color', String(m.button_color)); if (m.button_hover_color) root.style.setProperty('--button-hover-color', String(m.button_hover_color)); if (m.card_bg) root.style.setProperty('--card-bg', String(m.card_bg));
    const f = t?.footer || {}; if (f.bg_color) root.style.setProperty('--footer-bg', String(f.bg_color)); if (f.text_color) root.style.setProperty('--text-color', String(f.text_color)); if (f.link_color) root.style.setProperty('--link-color', String(f.link_color)); if (f.link_hover_color) root.style.setProperty('--link-hover-color', String(f.link_hover_color)); if (f.icon_color) root.style.setProperty('--icon-color', String(f.icon_color)); if (f.height) root.style.setProperty('--footer-height', `${f.height}px`); if (f.layout) root.style.setProperty('--footer-layout', String(f.layout));
    const ty = t?.typography || {}; if (ty.font_family) root.style.setProperty('--font-family', String(ty.font_family)); if (ty.h1_size) root.style.setProperty('--h1-size', `${ty.h1_size}px`); if (ty.h2_size) root.style.setProperty('--h2-size', `${ty.h2_size}px`); if (ty.h3_size) root.style.setProperty('--h3-size', `${ty.h3_size}px`); if (ty.body_size) root.style.setProperty('--body-size', `${ty.body_size}px`); if (ty.weight_normal) root.style.setProperty('--weight-normal', String(ty.weight_normal)); if (ty.weight_bold) root.style.setProperty('--weight-bold', String(ty.weight_bold));
    const r = t?.radius || {}; if (r.base) root.style.setProperty('--radius-base', `${r.base}px`); if (r.button) root.style.setProperty('--radius-button', `${r.button}px`);
    const s = t?.shadow || {}; if (s.color) root.style.setProperty('--shadow-color', String(s.color)); if (s.intensity != null) root.style.setProperty('--shadow-intensity', String(s.intensity));
    const an = t?.animation || {}; if (an.speed != null) root.style.setProperty('--anim-speed', String(an.speed));
    const sp = t?.spacing || {}; if (sp.base) root.style.setProperty('--spacing-base', `${sp.base}px`); if (sp.section_padding) root.style.setProperty('--section-padding', `${sp.section_padding}px`);
  }, []);

  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 2000], [0, 400]);
  const y2 = useTransform(scrollY, [0, 2000], [0, -400]);

  const openContactPage = () => {
    if (typeof window !== 'undefined') {
      window.location.href = '/contactform';
    }
  };

  // Check if running in iframe (Live Preview)
  const isIframe = typeof window !== 'undefined' && window.self !== window.top;

  // Skip loading screen if in iframe
  useEffect(() => {
    if (isIframe) {
      setIsLoading(false);
    }
    
    // Safety timeout: if video takes too long (>8s), just let the user in
    const safetyTimer = setTimeout(() => {
      setVideoLoaded(true);
    }, 8000);

    return () => {
      clearTimeout(safetyTimer);
    };
  }, [isIframe]);

  // Fetch hero sections from backend
  useEffect(() => {
    const fetchHero = async () => {
      const now = new Date().toISOString();
      const defaults: AppHeroSection[] = [
        { id: 'exhibitions', heading: 'Exhibitions', description: 'Immersive environments that captivate and engage.', type: 'tesseract', gradient: 'from-cyan-400 to-blue-600', display_order: 1, is_active: true, created_at: now, updated_at: now },
        { id: 'events', heading: 'Events', description: 'Pulse-pounding visuals synchronized with sound.', type: 'nova', gradient: 'from-pink-500 to-purple-600', display_order: 2, is_active: true, created_at: now, updated_at: now },
        { id: 'fit-out', heading: 'Fit-out', description: 'Architecture meets Algorithm.', type: 'grid', gradient: 'from-emerald-400 to-teal-600', display_order: 3, is_active: true, created_at: now, updated_at: now }
      ];
      try {
        const res = await heroApi.getAll();
        if (res?.success && Array.isArray(res.data) && res.data.length) {
          const gradients = ['from-cyan-400 to-blue-600', 'from-pink-500 to-purple-600', 'from-emerald-400 to-teal-600'];
          interface BackendHeroRow {
            id?: string | number;
            heading?: string;
            title?: string;
            description?: string;
            subtitle?: string;
            type?: 'tesseract' | 'nova' | 'grid' | string;
            gradient?: string;
            display_order?: number;
            is_active?: boolean;
            created_at?: string;
            updated_at?: string;
            use_type?: boolean;
            heading_color?: string;
            description_color?: string;
            description_bg_color?: string;
            button_text?: string;
            button_action?: 'modal'|'link';
            button_link?: string | null;
            button_bg?: string | null;
            button_text_color?: string | null;
            button_hover_gradient_start?: string | null;
            button_hover_gradient_end?: string | null;
            heading_overlay_color?: string;
            heading_shadow_color?: string;
          }
          const mapped: AppHeroSection[] = res.data.map((row: BackendHeroRow, idx: number) => ({
            id: String(row.id ?? idx + 1),
            heading: row.heading ?? row.title ?? 'Hero',
            description: row.description ?? row.subtitle ?? '',
            type: row.type ?? (idx % 3 === 0 ? 'tesseract' : idx % 3 === 1 ? 'nova' : 'grid'),
            gradient: row.gradient ?? gradients[idx % gradients.length],
            display_order: row.display_order ?? (idx + 1),
            is_active: row.is_active ?? true,
            created_at: row.created_at ?? now,
            updated_at: row.updated_at ?? now,
            use_type: row.use_type ?? undefined,
            heading_color: row.heading_color ?? undefined,
            description_color: row.description_color ?? undefined,
            description_bg_color: row.description_bg_color ?? undefined,
            button_text: row.button_text ?? 'Start Project',
            button_action: row.button_action ?? 'modal',
            button_link: row.button_link ?? null,
            button_bg: row.button_bg ?? null,
            button_text_color: row.button_text_color ?? null,
            button_hover_gradient_start: row.button_hover_gradient_start ?? null,
            button_hover_gradient_end: row.button_hover_gradient_end ?? null,
            heading_overlay_color: row.heading_overlay_color ?? undefined,
            heading_shadow_color: row.heading_shadow_color ?? undefined,
            heading_overlay_enabled: (row as any).heading_overlay_enabled ?? undefined,
            heading_shadow_enabled: (row as any).heading_shadow_enabled ?? undefined
        }));
        setHeroContent(mapped);
          preloadHeroVideo(mapped);
        } else {
          setHeroContent(defaults);
          preloadHeroVideo(defaults);
        }
      } catch {
        setHeroContent(defaults);
        preloadHeroVideo(defaults);
      }
    };
    fetchHero();
  }, []);

  // Real-time sync: listen to localStorage updates and periodic polling
  useEffect(() => {
    interface BackendHeroRow {
      id?: string | number;
      heading?: string;
      title?: string;
      description?: string;
      subtitle?: string;
      type?: 'tesseract' | 'nova' | 'grid' | string;
      gradient?: string;
      display_order?: number;
      is_active?: boolean;
      created_at?: string;
      updated_at?: string;
      video_url?: string | null;
      video_type?: 'background' | 'foreground';
      video_settings?: unknown;
      use_video?: boolean;
      use_type?: boolean;
      heading_color?: string;
      description_color?: string;
      description_bg_color?: string;
      button_text?: string;
      button_action?: 'modal'|'link';
      button_link?: string | null;
      button_bg?: string | null;
      button_text_color?: string | null;
      button_hover_gradient_start?: string | null;
      button_hover_gradient_end?: string | null;
      heading_overlay_color?: string;
      heading_shadow_color?: string;
    }
    const applyData = (rows: BackendHeroRow[]) => {
      const now = new Date().toISOString();
      const gradients = ['from-cyan-400 to-blue-600', 'from-pink-500 to-purple-600', 'from-emerald-400 to-teal-600'];
      const mapped: AppHeroSection[] = (rows || []).map((row: BackendHeroRow, idx: number) => ({
        id: String(row.id ?? idx + 1),
        heading: row.heading ?? row.title ?? 'Hero',
        description: row.description ?? row.subtitle ?? '',
        type: row.type ?? (idx % 3 === 0 ? 'tesseract' : idx % 3 === 1 ? 'nova' : 'grid'),
        gradient: row.gradient ?? gradients[idx % gradients.length],
        display_order: row.display_order ?? (idx + 1),
        is_active: row.is_active ?? true,
        created_at: row.created_at ?? now,
        updated_at: row.updated_at ?? now,
        video_url: row.video_url ?? null,
        video_type: row.video_type ?? undefined,
        video_settings: row.video_settings ?? undefined,
        use_video: row.use_video ?? undefined,
        use_type: row.use_type ?? undefined,
        heading_color: row.heading_color ?? undefined,
        description_color: row.description_color ?? undefined,
        description_bg_color: row.description_bg_color ?? undefined,
        button_text: row.button_text ?? 'Start Project',
        button_action: row.button_action ?? 'modal',
        button_link: row.button_link ?? null,
        button_bg: row.button_bg ?? null,
        button_text_color: row.button_text_color ?? null,
        button_hover_gradient_start: row.button_hover_gradient_start ?? null,
        button_hover_gradient_end: row.button_hover_gradient_end ?? null,
        heading_overlay_color: row.heading_overlay_color ?? undefined,
        heading_shadow_color: row.heading_shadow_color ?? undefined,
        heading_overlay_enabled: (row as any).heading_overlay_enabled ?? undefined,
        heading_shadow_enabled: (row as any).heading_shadow_enabled ?? undefined,
      }));
      if (mapped.length) {
        setHeroContent(mapped);
        preloadHeroVideo(mapped);
      }
    };

    const onStorage = (e: StorageEvent) => {
      if (e.key === 'heroSections' && e.newValue) {
        try { applyData(JSON.parse(e.newValue)); } catch { void 0; }
      }
    };
    window.addEventListener('storage', onStorage);

    let timer: number | undefined;
    const startPolling = () => {
      timer = window.setInterval(async () => {
        try { const res = await heroApi.getAll(); if (res?.success && Array.isArray(res.data)) applyData(res.data as BackendHeroRow[]); } catch { void 0; }
      }, 10000);
    };
    startPolling();
    return () => {
      window.removeEventListener('storage', onStorage);
      if (timer) window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    const defaults: ThemeSettings = {
      primary_color: '#ffffff',
      secondary_color: '#a3a3a3',
      background_color: '#030014',
      text_color: '#ffffff',
      button_color: '#ffffff',
      button_hover_color: '#e5e5e5',
      header_color: '#0b0c15',
      footer_color: '#050505',
      mode: 'dark'
    };
    const loadTheme = async () => {
      try {
        let isOnline = false;
        try {
          const API = (typeof window !== 'undefined' && window.location?.origin)
            ? `${window.location.origin.replace(/\/+$/, '')}/api`
            : '/api';
          const h = await fetch(`${API}/health`);
          const hj = await h.json();
          isOnline = !!hj.success;
        } catch { /* offline */ }
        if (isOnline) {
          const res = await import('./services/api');
          const api = res;
          const ts = await api.themeApi.get();
          if (ts?.success && ts.data) {
            applyTheme(ts.data as Partial<ThemeConfig>);
            try { localStorage.setItem('themeSettings', JSON.stringify(ts.data)); } catch { void 0; }
          } else {
            applyTheme(defaults);
          }
        } else {
          applyTheme(defaults);
        }
      } catch {
        applyTheme(defaults);
      }
    };
    loadTheme();
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'themeSettings' && e.newValue) {
        try { const v = JSON.parse(e.newValue) as Partial<ThemeConfig>; applyTheme(v); } catch { void 0; }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [applyTheme]);

  return (
    <div className="min-h-screen bg-theme text-theme selection:bg-pink-500 selection:text-white">
      <AnimatePresence mode="wait">
        {isLoading && <Preloader onLoadingComplete={() => setIsLoading(false)} waitForExternal={!videoLoaded} />}
      </AnimatePresence>

      {!isLoading && (
        <BrowserRouter>
          {isRevealing && (
            <motion.div
              initial={{ top: '45%', right: '45%', bottom: '45%', left: '45%', borderRadius: '10px', opacity: 1 }}
              animate={{ top: '0%', right: '0%', bottom: '0%', left: '0%', borderRadius: '0px', opacity: 0 }}
              transition={{ 
                duration: 1.8, 
                ease: [0.77, 0, 0.175, 1],
                opacity: { times: [0, 0.8, 1], duration: 1.8, ease: "linear" }
              }}
              style={{ border: '2px solid #FFBF00', boxShadow: '0 0 20px rgba(249, 115, 22, 0.4)' }}
              className="fixed z-[9999] pointer-events-none"
            />
          )}
          
          {/* Navbar and Cursor moved outside the reveal container to prevent 'fixed' positioning issues */}
          {!isIframe && <Cursor />}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: isRevealing ? 0 : 1 }}
            transition={{ duration: 0.5, delay: 1.2 }}
            className="relative z-[100]"
          >
            <ConditionalNavbar onContactClick={openContactPage} />
          </motion.div>

          <motion.div
            initial={{ clipPath: 'inset(45% 45% 45% 45% round 10px)', filter: 'blur(10px)' }}
            animate={isRevealing ? { clipPath: 'inset(0% 0% 0% 0% round 0px)', filter: 'blur(0px)' } : { clipPath: 'none', filter: 'none' }}
            transition={{ duration: 1.8, ease: [0.77, 0, 0.175, 1] }}
            onAnimationComplete={() => setIsRevealing(false)}
            className={isRevealing ? "fixed inset-0 w-full h-full overflow-hidden bg-theme" : "w-full min-h-screen bg-theme"}
          >
            <ScrollToTop />
            <RouterContent
              isIframe={isIframe}
              openContactPage={openContactPage}
              y1={y1}
              y2={y2}
              heroContent={heroContent}
              updateHeroFromDashboard={updateHeroFromDashboard}
              videoBlobUrl={videoBlobUrl}
            />
          </motion.div>
        </BrowserRouter>
      )}
    </div>
  );
};

const LoginPage = () => {
  const navigate = useNavigate();
  return <Login onLoginSuccess={() => navigate('/dashboard', { replace: true })} />;
};

const RouterContent: React.FC<{ isIframe: boolean; openContactPage: () => void; y1: unknown; y2: unknown; heroContent: AppHeroSection[]; updateHeroFromDashboard: (d: AppHeroSection[]) => void; videoBlobUrl: string | null }> = ({ isIframe, openContactPage, y1, y2, heroContent, updateHeroFromDashboard, videoBlobUrl }) => {
  const location = useLocation();
  const isDashboard = location.pathname.startsWith('/dashboard');
  const isLogin = location.pathname === '/login';

  if (isDashboard || isLogin) {
    return (
      <div className="cursor-auto">
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={<></>} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/project" element={<ProjectsPage />} />
          <Route path="/our-services" element={<ServicesPage />} />
          <Route path="/our-about" element={<AboutPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/projects/:slug" element={<ProjectDetail />} />
          <Route path="/project/single-page" element={<SingleProjectPage />} />
          <Route path="/project/single-page/:slug" element={<SingleProjectPage />} />
          <Route path="/dashboard" element={<ProtectedDashboard heroContent={heroContent} onUpdateHeroContent={updateHeroFromDashboard} />} />
          <Route path="/dashboard/:section" element={<ProtectedDashboard heroContent={heroContent} onUpdateHeroContent={updateHeroFromDashboard} />} />
          <Route path="/dashboard/builder/new" element={<PageBuilder />} />
          <Route path="/test-hero" element={<TestHero />} />
          <Route path="/simple-test" element={<SimpleHeroTest />} />
          <Route path="/dashboard/builder/:id" element={<PageBuilder />} />
        </Routes>
      </div>
    );
  }
  return (
    <>
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key="site"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className={isIframe ? "cursor-auto" : "cursor-none"}
        >
          <Routes>
            <Route
              path="/"
              element={
                <>
                  <main className="relative">
                    <Hero onContactClick={openContactPage} data={heroContent} videoBlobUrl={videoBlobUrl} />
                    <Services />
                    <Portfolio />
                    <SectionAI />
                  </main>
                </>
              }
            />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/project" element={<ProjectsPage />} />
            <Route path="/portfolio" element={<ProjectsPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/our-services" element={<ServicesPage />} />
              <Route path="/our-about" element={<AboutPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/projects/:slug" element={<ProjectDetail />} />
            <Route path="/portfolio/:slug" element={<PortfolioDetailPage />} />
            <Route path="/service/:slug" element={<ServiceDetail />} />
            <Route path="/contactform" element={<ContactFormPage />} />
            <Route path="/contact" element={<ContactFormPage />} />
            <Route path="/hero-reel" element={<HeroReelPage />} />
            <Route path="/pages/:slug" element={<PageViewer />} />
            <Route path="/project/single-page" element={<SingleProjectPage />} />
            <Route path="/dashboard" element={<ProtectedDashboard heroContent={heroContent} onUpdateHeroContent={updateHeroFromDashboard} />} />
            <Route path="/page/:slug" element={<DynamicPage />} />
            <Route path="*" element={<Error404 />} />
          </Routes>
          <Footer onContactClick={openContactPage} />
          <FloatingWhatsApp />
        </motion.div>
      </AnimatePresence>
    </>
  );
};

const ProtectedDashboard: React.FC<{ heroContent: AppHeroSection[]; onUpdateHeroContent: (d: AppHeroSection[]) => void }> = ({ heroContent, onUpdateHeroContent }) => {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => { try { return localStorage.getItem('token'); } catch { return null; } });

  useEffect(() => {
    const handleLogout = () => {
      setToken(null);
    };
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  if (!token) {
    return <Login onLoginSuccess={() => {
      setToken(localStorage.getItem('token'));
      navigate('/dashboard', { replace: true });
    }} />;
  }
  const handleUpdateFromDash = (data: unknown[]) => {
    onUpdateHeroContent(data as AppHeroSection[]);
  };
  return (
    <ModernDashboard
      onBackToSite={() => navigate('/', { replace: true })}
    />
  );
};

export default App;

